import React, { useState, useEffect, useRef } from "react";
import postsStyles from "./personalposts.module.css";
import { HiOutlineLightBulb } from "react-icons/hi";
import { FaRegPaperPlane } from "react-icons/fa";
import { CiMenuKebab } from "react-icons/ci";
import ReactPlayer from "react-player";
import defaultImage from "../../appImges/avatartTemplate/default_img.png";
import EditComment from "./EditComment";
import { RxCross2 } from "react-icons/rx";
import { GoVideo } from "react-icons/go";
import { toast } from "react-toastify";
import { format } from "date-fns";
import ModalComp from "../popUp/ModalComp";
import axios from "axios";
import { FiShare2 } from "react-icons/fi";
import { useUser } from "../../storage/UserContext"; // context hook
import AdminDropdown from "../../components/adminDropdown/AdminDropdown";
const PersonalPosts = ({
  role,
  postsData,
  username,
  userImage,
  checkTheSameUser,
}) => {
  /**
   * This component is responsible for the display, creation, editing, and deletion of posts and comments. It also allows the active user to vote for a post, and share a post. The component is used in the ProfilePage and OtherUserProfilePage pages.
   */

  const apiUrl = process.env.REACT_APP_URL;

  const baseUrl = `${apiUrl}`;
  const [expandedPosts, setExpandedPosts] = useState({});
  const [posts, setPosts] = useState(postsData);
  const [newComment, setNewComment] = useState({});
  const [dropdowns, setDropdowns] = useState({});
  const dropdownRef = useRef(null);
  const [editCommentData, setEditCommentData] = useState({
    isOpen: false,
    commentId: null,
    postId: null,
    content: "",
  });

  const { userData, setUserData } = useUser(); // Using the context
  // Delete post modal state
  const [isModalOpen, setIsModalOpen] = useState(false); // Modal state
  const [postToDelete, setPostToDelete] = useState(null); // Post to be deleted
  // Edit post modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  // const [postToEdit, setPostToEdit] = useState(null);
  const [postToEdit, setPostToEdit] = useState({
    post_id: null,
    content: "",
    post_img_link: null, // This will hold the file object
    post_img_url: "", // This will hold the temporary URL for preview
    post_video_link: "",
  });
  const [showVideoInput, setShowVideoInput] = useState(false);

  useEffect(() => {
    setPosts(postsData);
  }, [postsData]);

  /**
   * Toggle the comments view (expanded / minimized) of a given post.
   * @param {*} postId - the id of the post to toggle its comments
   */
  const toggleComments = (postId) => {
    setExpandedPosts((prev) => ({
      ...prev,
      [postId]: !prev[postId],
    }));
  };

  /**
   * Toggle the dropdown menu view (open / close) of a given comment.
   * @param {*} commentId - the id of the comment to toggle its dropdown menu.
   */
  const toggleDropdown = (commentId) => {
    setDropdowns((prev) => ({
      ...prev,
      [commentId]: !prev[commentId],
    }));
  };

  /**
   * Validate a new comment, and if valid - add it to the database and the page display.
   * @param {*} postId - the id of the post to add the comment to
   */
  const handleNewComment = async (postId) => {
    try {
      if (!newComment[postId]) {
        return;
      }

      const response = await axios.post(
        `${apiUrl}/comments/create`,
        { postId, content: newComment[postId] },
        { withCredentials: true }
      );

      if (response.status === 200) {
        const newCommentId = response.data.commentId;
        const updatedPosts = posts.map((post) =>
          post.post_id === postId
            ? {
                ...post,
                comments: [
                  ...post.comments,
                  {
                    comment_id: newCommentId,
                    comment_content: newComment[postId],
                    commenter: checkTheSameUser
                      ? username
                      : userData.username || defaultImage,
                    commenter_img_link: checkTheSameUser
                      ? userImage
                      : userData.img_link || defaultImage,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                  },
                ],
              }
            : post
        );

        setPosts(updatedPosts);
        setNewComment((prev) => ({ ...prev, [postId]: "" }));
      }
    } catch (e) {
      console.error("Failed to add new comment", e);
    }
  };

  /**
   * Handle a voting action to a post. Check if the post has already been voted for by the same user, and if so - cancel the vote. If not, add the new vote.
   * @param {*} postId - the id of the post to handle the vote for.
   */
  const addDeleteVote = async (postId) => {
    try {
      const response = await axios.post(
        `${apiUrl}/posts/like`,
        {
          post_id: postId,
          username: checkTheSameUser ? username : userData.username,
        },
        { withCredentials: true }
      );

      if (response.status === 200) {
        const updatedPosts = posts.map((post) =>
          post.post_id === postId
            ? { ...post, votes: response.data.votes }
            : post
        );
        setPosts(updatedPosts);
      }
    } catch (error) {
      console.error("Error updating vote:", error);
    }
  };

  /**
   * Delete a comment from the database and the page.
   * @param {*} commentId - the id of the comment to delete.
   * @param {*} postId - the id of the post to which the comment belong.
   */
  const handleDeleteComment = async (commentId, postId) => {
    try {
      const response = await axios.put(
        `${apiUrl}/comments/deactivate/${commentId}`,
        {},
        { withCredentials: true }
      );

      if (response.status === 200) {
        const updatedPosts = posts.map((post) => {
          if (post.post_id === postId) {
            return {
              ...post,
              comments: post.comments.filter(
                (comment) => comment.comment_id !== commentId
              ),
            };
          }
          return post;
        });
        setPosts(updatedPosts);
        setDropdowns((prev) => ({
          ...prev,
          [commentId]: false,
        }));
      }
    } catch (error) {
      console.error("Error deleting comment:", error);
    }
  };

  /**
   * Set the current editCommentData to the data of a given comment, so its content can be displayed for editing.
   * @param {*} commentId - the id of the comment to edit.
   * @param {*} postId - the id of the post to which the comment belong.
   * @param {*} content - the comment text.
   */
  const handleEditComment = (commentId, postId, content) => {
    setDropdowns((prev) => ({
      ...prev,
      [commentId]: false,
    }));
    setEditCommentData({
      isOpen: true,
      commentId,
      postId,
      content,
    });
  };

  /**
   * Reset the current editCommentData, given an edit comment cancellation.
   */
  const handleCloseEditComment = () => {
    setEditCommentData({
      isOpen: false,
      commentId: null,
      postId: null,
      content: "",
    });
  };

  /**
   * Validate the current editCommentData, and if valid - update it in the database and the page display.
   */
  const handleConfirmEditComment = async () => {
    try {
      const { commentId, postId, content } = editCommentData;

      if (!content) {
        return;
      }

      const response = await axios.put(
        `${apiUrl}/comments/update/${commentId}`,
        { content },
        { withCredentials: true }
      );

      if (response.status === 200) {
        const updatedPosts = posts.map((post) => {
          if (post.post_id === postId) {
            return {
              ...post,
              comments: post.comments.map((comment) =>
                comment.comment_id === commentId
                  ? {
                      ...comment,
                      comment_content: content,
                      updated_at: new Date().toISOString(),
                    }
                  : comment
              ),
            };
          }
          return post;
        });
        setPosts(updatedPosts);
        handleCloseEditComment();
      }
    } catch (error) {
      console.error("Error editing comment:", error);
    }
  };

  /**
   * Show confirmation modal for deleting a post (soft delete), or directly cancel a post share.
   * @param {*} type - string: "share" if this is a post shared by the current user, or "post" if the current user published it.
   * @param {*} post_id - the id of the post to show confirmation modal before deletion of, or to cancel share of.
   * @param {*} share_id - the id of the post share (if relevant)
   */
  const handleDeactivatePostOrShare = async (type, post_id, share_id) => {
    try {
      let response = "";

      // Handle shares: Directly delete without modal
      if (type === "share") {
        response = await axios.delete(
          `${apiUrl}/posts/cancel_share/${share_id}`,
          { withCredentials: true }
        );

        if (response.status === 200) {
          const updatedPosts = posts.filter((post) => post.post_id !== post_id);
          setPosts(updatedPosts);
        } else {
          console.error("Failed to deactivate the share:", response.statusText);
        }
      }

      // Handle posts: Open modal for confirmation
      else if (type === "post") {
        setPostToDelete({ type, post_id }); // Set the post to be deleted
        setIsModalOpen(true); // Open the confirmation modal
        // Close the dropdown for the specific post
        setDropdowns((prev) => ({
          ...prev,
          [post_id]: false,
        }));
      }
    } catch (error) {
      console.error("Error deleting post or share:", error);
    }
  };

  /**
   * Delete a post (soft delete - mark as inactive) from the database and the page display.
   */
  const handlePostToDeactivate = async () => {
    if (!postToDelete) return;

    try {
      const response = await axios.put(
        `${apiUrl}/posts/deactivate/${postToDelete.post_id}`,
        {},
        { withCredentials: true }
      );

      if (response.status === 200) {
        const updatedPosts = posts.filter(
          (post) => post.post_id !== postToDelete.post_id
        );
        setPosts(updatedPosts);
      } else {
        console.error("Failed to deactivate the post:", response.statusText);
      }
    } catch (error) {
      console.error("Error deleting post:", error);
    } finally {
      setIsModalOpen(false); // Close the modal
      setPostToDelete(null); // Clear the post to delete state
    }
  };

  /**
   * Open the post edit modal, to allow the user to edit a post.
   * @param {*} post - object - the post to edit
   */
  const handleEditPost = (post) => {
    const validImageUrl = post.post_img_link ? post.post_img_link : "";
    setPostToEdit({
      post_id: post.post_id,
      content: post.content,
      post_img_link: null, // Reset file object
      post_img_url: validImageUrl,
      post_video_link: post.post_video_link,
    });
    setIsEditModalOpen(true);
    setDropdowns((prev) => ({ ...prev, [post.post_id]: false }));
  };

  /**
   * Update a post content in the database and on the page display. The updated post is stored in postToEdit.
   */
  const handleUpdatePost = async () => {
    const { post_id, content, post_img_link, post_video_link, post_img_url } =
      postToEdit;
    // Convert empty video link to null
    const sanitizedVideoLink = post_video_link ? post_video_link : null;
    try {
      const formData = new FormData();
      formData.append("username", username);
      formData.append("content", content);
      if (post_video_link) {
        formData.append("video_link", sanitizedVideoLink);
      }

      let imageURL = post_img_url;
      if (post_img_link instanceof File) {
        formData.append("img_link", post_img_link);
        imageURL = URL.createObjectURL(post_img_link);
      } else {
        imageURL = post_img_url;
      }

      const response = await axios.put(
        `${apiUrl}/posts/update/${post_id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true,
        }
      );

      if (response.status === 200) {
        const updatedPost = response.data.post;
        const updatedPosts = posts.map((post) =>
          post.post_id === post_id
            ? {
                ...post,
                content: updatedPost.content,
                post_img_link: updatedPost.post_img_link || imageURL,
                post_video_link: updatedPost.post_video_link,
              }
            : post
        );
        setPosts(updatedPosts);
        setIsEditModalOpen(false);
        setPostToEdit(null);
      } else {
        console.error("Failed to update the post:", response.statusText);
      }
    } catch (error) {
      console.error("Error updating post:", error);
    } finally {
      setIsEditModalOpen(false);
      setPostToEdit(null);
    }
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setPostToEdit((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPostToEdit((prev) => ({
        ...prev,
        post_img_link: file,
        post_img_url: URL.createObjectURL(file), // Create temporary URL
      }));
    } else {
      setPostToEdit((prev) => ({
        ...prev,
        post_img_url: prev.post_img_url, // Preserve the existing image URL if no new file is selected
      }));
    }
  };

  // Function to change the view permissions of a post (public/private)
  const handleChangeViewPermissions = async (postId, permission) => {
    try {
      const response = await axios.put(
        `${apiUrl}/posts/viewing_permission/${postId}`,
        { viewingPermission: !permission },
        { withCredentials: true }
      );

      if (response.status === 200) {
        const updatedPosts = posts.map((post) =>
          post.post_id === postId
            ? { ...post, viewing_permission: !permission }
            : post
        );
        setPosts(updatedPosts);
        toast.success("View permissions updated successfully!");
      } else {
        console.error(
          "Failed to change view permissions:",
          response.data.message
        );
      }
    } catch (error) {
      console.error("Error changing view permissions: ", error);
      // Add user feedback here
    }
  };

  /* Function to change the comment permissions of a post - whether it is allowed or not to post new comments to the post. The existing comments will continue to be displayed in any case. */
  const handleChangeCommentPermissions = async (postId, permission) => {
    try {
      const response = await axios.put(
        `${apiUrl}/posts/comment_permission/${postId}`,
        { commentPermission: !permission },
        { withCredentials: true }
      );

      if (response.status === 200) {
        const updatedPosts = posts.map((post) =>
          post.post_id === postId
            ? { ...post, comment_permission: !permission }
            : post
        );
        setPosts(updatedPosts);
        toast.success("Comment permissions updated successfully!");
      } else {
        console.error(
          "Failed to change comment permissions:",
          response.data.message
        );
      }
    } catch (error) {
      console.error("Error changing view permissions: ", error);
      // Add user feedback here
    }
  };

  /**
   * Share a post by the current user (show it to its followers and other profile viewers).
   * @param {*} postId - the id of the post to share.
   */
  const handleSharePost = async (postId) => {
    try {
      const response = await axios.post(
        `${apiUrl}/posts/share`,
        { postId: postId, username: userData.username },
        { withCredentials: true }
      );

      if (response.status === 200) {
        toast.success("Post shared successfully");
      }
    } catch (error) {
      if (error.response && error.response.status === 409) {
        toast.warning("Post has already been shared");
      } else {
        console.error("Error sharing post:", error);
        toast.error("Error sharing post");
      }
    }
  };

  /**
   * Convert a MySQL timestamp to a readable date string in the format: DD/MM/YYYY hh:mm.
   * @param {*} dateString - a MySQL Timestamp to convert
   * @returns a date string in the format: DD/MM/YYYY hh:mm.
   */
  function formatDate(dateString) {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }
  // Function to open the delete modal
  const openDeleteModal = (postId) => {
    setPostToDelete({ type: "post", post_id: postId }); // Set the post to be deleted
    setIsModalOpen(true); // Open the confirmation modal
  };

  return (
    <div className={postsStyles.mainPostsStyle}>
      {posts.map((post) => {
        return (
          <div
            key={post.post_id}
            className={`${postsStyles.postStyle} ${
              post.type === "share" ? postsStyles.sharedPost : ""
            }`}
          >
            <div className={postsStyles.headerPost}>
              <div>
                <figure className={postsStyles.avatar}>
                  <img
                    src={userImage ? userImage : defaultImage}
                    alt={username}
                    onError={(e) => {
                      e.target.src = defaultImage;
                      e.target.onerror = null;
                    }}
                  />
                  <figcaption>{username}</figcaption>
                </figure>
                <p className={postsStyles.postDate}>
                  <span>{format(new Date(post.created_at), "dd/MM/yyyy")}</span>
                  <span>{new Date(post.created_at).toLocaleTimeString()}</span>
                </p>
              </div>{" "}
              {checkTheSameUser ? (
                // If checkTheSameUser is true, show the dropdown with delete/edit options
                post.type === "post" ? (
                  <div
                    ref={(el) => {
                      if (!dropdownRef.current) {
                        dropdownRef.current = {};
                      }
                      dropdownRef.current[post.post_id] = el;
                    }}
                    className={postsStyles.dropdownContainer}
                  >
                    <CiMenuKebab
                      className={postsStyles.dropdownTrigger}
                      onClick={() => toggleDropdown(post.post_id)}
                    />
                    {dropdowns[post.post_id] && (
                      <div className={postsStyles.dropdownMenu}>
                        <p
                          onClick={() =>
                            handleDeactivatePostOrShare(
                              post.type,
                              post.post_id,
                              post.share_id
                            )
                          }
                        >
                          Delete
                        </p>
                        <p onClick={() => handleEditPost(post)}>Edit</p>
                        <p
                          onClick={() =>
                            handleChangeViewPermissions(
                              post.post_id,
                              post.viewing_permission
                            )
                          }
                        >
                          {post.viewing_permission
                            ? "Make Post Visible to Pals Only"
                            : "Make Post Public"}
                        </p>
                        <p
                          onClick={() =>
                            handleChangeCommentPermissions(
                              post.post_id,
                              post.comment_permission
                            )
                          }
                        >
                          {post.comment_permission
                            ? "Disable New Comments"
                            : "Enable New Comments"}
                        </p>
                      </div>
                    )}

                    {/* Modal for edit post */}
                    <ModalComp
                      isOpen={isEditModalOpen}
                      onClose={() => setIsEditModalOpen(false)}
                    >
                      <div className={postsStyles.editForm}>
                        <div>
                          <label>Content:</label>
                          <textarea
                            name="content"
                            value={postToEdit?.content || ""}
                            onChange={handleEditInputChange}
                          />
                        </div>

                        <div>
                          {postToEdit?.post_img_url ? (
                            <div>
                              <img
                                src={postToEdit.post_img_url} // Use the temporary URL or existing URL
                                alt="Post"
                              />
                              <input
                                type="file"
                                name="post_img_link"
                                onChange={handleEditFileChange}
                              />
                            </div>
                          ) : (
                            <div>
                              <input
                                type="file"
                                name="post_img_link"
                                onChange={handleEditFileChange}
                              />
                            </div>
                          )}
                        </div>
                        <div className={postsStyles.videoInputContainer}>
                          <span
                            className={postsStyles.videoBtn}
                            onClick={() => setShowVideoInput(!showVideoInput)}
                          >
                            <GoVideo />
                          </span>
                          {showVideoInput && (
                            <input
                              type="text"
                              name="post_video_link"
                              value={postToEdit?.post_video_link || ""}
                              onChange={handleEditInputChange}
                              className={postsStyles.inputVideoLink}
                            />
                          )}
                        </div>
                        {postToEdit?.post_video_link && (
                          <div className={postsStyles.videoContainer}>
                            <ReactPlayer
                              url={postToEdit.post_video_link}
                              controls={true}
                              className={postsStyles.reactPlayer}
                              width="100%"
                              height="100%"
                            />
                          </div>
                        )}
                        <button onClick={handleUpdatePost}>Update Post</button>
                      </div>
                    </ModalComp>
                  </div>
                ) : (
                  <span
                    className={postsStyles.deleteBtn}
                    onClick={() =>
                      handleDeactivatePostOrShare(
                        post.type,
                        post.post_id,
                        post.share_id
                      )
                    }
                  >
                    <RxCross2 />
                  </span>
                )
              ) : (
                post.author !== userData.username &&
                // If checkTheSameUser is false, show the AdminDropdown for Admin or share icon for others
                (role === "Admin" ? (
                  <AdminDropdown
                    openDeleteModal={openDeleteModal} // Pass the function to open the delete modal
                    deletePost={handlePostToDeactivate} // Function to handle the delete action
                    handleShare={handleSharePost} // Function to handle the share action
                    postId={post.post_id} // Pass the post ID
                  />
                ) : (
                  // Show the share icon for non-admin users
                  <p
                    className={postsStyles.sharePost}
                    onClick={() => handleSharePost(post.post_id)}
                  >
                    <FiShare2 className={postsStyles.shareIcon} />
                  </p>
                ))
              )}
            </div>
            {/* Post main content  */}
            <section
              className={`${postsStyles.postData} ${
                post.type === "share" ? postsStyles.sharedPostContent : ""
              }`}
            >
              {post.type === "share" && (
                <div className={postsStyles.sharedPostInfo}>
                  <span>Posted by: {post.author}</span>
                </div>
              )}
              <div>{post.content}</div>
            </section>
            <figure className={postsStyles.postPic}>
              {post.post_img_link ? (
                <img
                  src={
                    post.post_img_link.startsWith("http")
                      ? post.post_img_link
                      : `${baseUrl}/${post.post_img_link}`
                  }
                  alt="post pic"
                />
              ) : null}
            </figure>
            {post?.post_video_link && (
              <div className={postsStyles.postVideo}>
                <ReactPlayer
                  url={post.post_video_link}
                  controls={true}
                  width="100%"
                  height="130%"
                />
              </div>
            )}
            <div className={postsStyles.likes}>
              <span
                className={postsStyles.spanBulb}
                onClick={() => addDeleteVote(post.post_id)}
              >
                <HiOutlineLightBulb />
                {post.votes}
              </span>
            </div>
            {/* Post comments */}

            {(post.comments || post.comment_permission) && (
              <div className={postsStyles.comments}>
                {/* Show new comment input only if permission is granted */}
                {post.comment_permission === 1 && (
                  <div className={postsStyles.newCommentContainer}>
                    <textarea
                      onChange={(e) =>
                        setNewComment((prev) => ({
                          ...prev,
                          [post.post_id]: e.target.value,
                        }))
                      }
                      placeholder="Add a new comment..."
                      value={newComment[post.post_id] || ""}
                    />
                    <button
                      className={postsStyles.commentSubmitBtn}
                      onClick={() => handleNewComment(post.post_id)}
                    >
                      <FaRegPaperPlane />
                    </button>
                  </div>
                )}

                {/* Render comments based on expanded state */}
                {post.comments && expandedPosts[post.post_id]
                  ? post.comments.map((comment) => (
                      <div
                        key={comment.comment_id}
                        className={postsStyles.singleComment}
                      >
                        <figure className={postsStyles.commentAuthorInfo}>
                          <img
                            src={
                              comment.commenter_img_link
                                ? comment.commenter_img_link.startsWith("http")
                                  ? comment.commenter_img_link
                                  : `${baseUrl}/${comment.commenter_img_link}`
                                : defaultImage
                            }
                            alt={comment.commenter}
                            onError={(e) => {
                              e.target.src = defaultImage;
                              e.target.onerror = null;
                            }}
                          />
                          <figcaption>
                            <p>{comment.commenter}</p>
                            <p className={postsStyles.commentDate}>
                              {formatDate(comment.updated_at)}
                            </p>
                          </figcaption>
                        </figure>
                        <div className={postsStyles.commentData}>
                          <p>{comment.comment_content}</p>
                        </div>

                        {userData?.username && (
                          <>
                            {(role === "Admin" ||
                              comment.commenter === userData.username ||
                              post.author === userData.username) && (
                              <div className={postsStyles.dropdownContainer}>
                                <CiMenuKebab
                                  className={postsStyles.dropdownTrigger}
                                  onClick={() =>
                                    toggleDropdown(comment.comment_id)
                                  }
                                />
                                {dropdowns[comment.comment_id] && (
                                  <div className={postsStyles.dropdownMenu}>
                                    {(role === "Admin" ||
                                      comment.commenter === userData.username ||
                                      post.author === userData.username) && (
                                      <p
                                        onClick={() =>
                                          handleDeleteComment(
                                            comment.comment_id,
                                            post.post_id
                                          )
                                        }
                                      >
                                        Delete
                                      </p>
                                    )}
                                    {comment.commenter ===
                                      userData.username && (
                                      <p
                                        onClick={() =>
                                          handleEditComment(
                                            comment.comment_id,
                                            post.post_id,
                                            comment.comment_content
                                          )
                                        }
                                      >
                                        Edit
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ))
                  : post.comments &&
                    post.comments.slice(-1).map((comment) => (
                      <div
                        key={comment.comment_id}
                        className={postsStyles.singleComment}
                      >
                        <figure className={postsStyles.commentAuthorInfo}>
                          <img
                            src={
                              comment.commenter_img_link
                                ? comment.commenter_img_link.startsWith("http")
                                  ? comment.commenter_img_link
                                  : `${baseUrl}/${comment.commenter_img_link}`
                                : defaultImage
                            }
                            alt={comment.commenter}
                            onError={(e) => {
                              e.target.src = defaultImage;
                              e.target.onerror = null;
                            }}
                          />
                          <figcaption>
                            <p>{comment.commenter}</p>
                            <p className={postsStyles.commentDate}>
                              {formatDate(comment.updated_at)}
                            </p>
                          </figcaption>
                        </figure>
                        <div className={postsStyles.commentData}>
                          <p>{comment.comment_content}</p>
                        </div>

                        {userData?.username && (
                          <>
                            {(role === "Admin" ||
                              comment.commenter === userData.username ||
                              post.author === userData.username) && (
                              <div className={postsStyles.dropdownContainer}>
                                <CiMenuKebab
                                  className={postsStyles.dropdownTrigger}
                                  onClick={() =>
                                    toggleDropdown(comment.comment_id)
                                  }
                                />
                                {dropdowns[comment.comment_id] && (
                                  <div className={postsStyles.dropdownMenu}>
                                    {(role === "Admin" ||
                                      comment.commenter === userData.username ||
                                      post.author === userData.username) && (
                                      <p
                                        onClick={() =>
                                          handleDeleteComment(
                                            comment.comment_id,
                                            post.post_id
                                          )
                                        }
                                      >
                                        Delete
                                      </p>
                                    )}
                                    {comment.commenter ===
                                      userData.username && (
                                      <p
                                        onClick={() =>
                                          handleEditComment(
                                            comment.comment_id,
                                            post.post_id,
                                            comment.comment_content
                                          )
                                        }
                                      >
                                        Edit
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ))}

                {/* Show toggle button for comments if there are multiple comments */}
                {post.comments && post.comments.length > 1 && (
                  <p
                    className={postsStyles.toggleCommentsBtn}
                    onClick={() => toggleComments(post.post_id)}
                  >
                    {expandedPosts[post.post_id] ? "Hide all" : "See more"}
                  </p>
                )}
              </div>
            )}
          </div>
        );
      })}
      <EditComment
        isOpen={editCommentData.isOpen}
        onClose={handleCloseEditComment}
        onConfirm={handleConfirmEditComment}
        commentContent={editCommentData.content}
        onChangeContent={(content) =>
          setEditCommentData((prev) => ({ ...prev, content }))
        }
      />
      {/* Modal for delete confirmation */}
      <ModalComp isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <p className={postsStyles.deleteConfirmationPost}>
          Are you sure you want to delete this post?
        </p>
        <button className={postsStyles.button} onClick={handlePostToDeactivate}>
          Yes
        </button>{" "}
        <button
          className={postsStyles.button}
          onClick={() => setIsModalOpen(false)}
        >
          No
        </button>
      </ModalComp>
    </div>
  );
};

export default PersonalPosts;
